/**
 * Lotes de cálculo y cobertura del mes, para el panel.
 *
 * Va por las rutas DERIVADAS DEL CATÁLOGO (§6), las mismas que usa el agente.
 * Las tres consultas piden el mismo permiso --`batches:read`-- y responden a
 * la misma pregunta desde ángulos distintos: qué se ha calculado este mes.
 *
 * Aquí NO se escribe. Abrir, marcar listo o aplicar un lote son comandos con
 * confirmación (§8.2) y se hacen hablando con el agente; una segunda puerta
 * desde la pantalla sería justo lo que este diseño existe para no tener.
 */
import { queryOptions } from "@tanstack/react-query";

import { request } from "../lib/api-client.ts";
import { API_BASE } from "../lib/api-routes.ts";
import type { operations } from "../lib/api-types.ts";
import { collectPages } from "../lib/pages.ts";

type ListResponse =
  operations["find_calculation_batches"]["responses"][200]["content"]["application/json"]["data"];

export type BatchSummary = ListResponse["batches"][number];
export type BatchStatus = BatchSummary["status"];

type BatchResponse =
  operations["get_calculation_batch"]["responses"][200]["content"]["application/json"]["data"];

export type BatchView = BatchResponse;
export type BatchKpi = BatchResponse["kpis"][number];

type CoverageResponse =
  operations["get_period_coverage"]["responses"][200]["content"]["application/json"]["data"];

export type PeriodCoverage = CoverageResponse;
export type CoverageKpi = CoverageResponse["kpis"][number];

/**
 * Los lotes del mes, TODOS.
 *
 * De esta lista salen alertas del Resumen ("hay un lote listo esperando
 * revisión"), así que no vale quedarse con la primera página: se recorren
 * todas por `nextOffset` (1.3). Cuántos vienen por página lo decide el
 * backend.
 */
export const findBatches = (period?: string): Promise<ListResponse> =>
  collectPages(
    (offset) => {
      const params = new URLSearchParams({ offset: String(offset) });
      if (period !== undefined && period !== "") params.set("period", period);
      return request<ListResponse>(
        `${API_BASE}/queries/find_calculation_batches?${params.toString()}`,
      );
    },
    (pages) => ({
      ...(pages.at(-1) ?? EMPTY_BATCHES),
      batches: pages.flatMap((page) => page.batches),
      offset: 0,
    }),
  );

const EMPTY_BATCHES: ListResponse = {
  batches: [],
  total: 0,
  offset: 0,
  nextOffset: null,
};

export const getBatch = (batchId: string): Promise<BatchResponse> =>
  request<BatchResponse>(
    `${API_BASE}/queries/get_calculation_batch?batchId=${encodeURIComponent(batchId)}`,
  );

export const getPeriodCoverage = (period: string): Promise<CoverageResponse> =>
  request<CoverageResponse>(
    `${API_BASE}/queries/get_period_coverage?period=${encodeURIComponent(period)}`,
  );

export const batchesQuery = (period: string) =>
  queryOptions({
    queryKey: ["batches", period] as const,
    queryFn: () => findBatches(period),
  });

export const batchQuery = (batchId: string) =>
  queryOptions({
    queryKey: ["batch", batchId] as const,
    queryFn: () => getBatch(batchId),
  });

export const coverageQuery = (period: string) =>
  queryOptions({
    queryKey: ["coverage", period] as const,
    queryFn: () => getPeriodCoverage(period),
  });

const STATUS_LABELS: Record<BatchStatus, string> = {
  OPEN: "Abierto",
  READY: "Listo",
  APPLIED: "Aplicado",
  DISCARDED: "Descartado",
};

/** El sistema guarda los estados en inglés; la interfaz los enseña en español. */
export const batchStatusLabel = (status: BatchStatus): string =>
  STATUS_LABELS[status];

/** Los puntos se leen en la escala en la que la gente los dice. */
export const points = (value: number): string => value.toLocaleString("es");
